import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api, { money } from "@/lib/api";
import { openPdf } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Receipt, Loader2, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function Invoices() {
  const { user, isViewer } = useAuth();
  const [invs, setInvs] = useState(null);
  const [active, setActive] = useState(null);
  const [paying, setPaying] = useState(false);

  const load = () => api.get("/invoices").then(({ data }) => setInvs(data)).catch(() => setInvs([]));
  useEffect(() => { load(); }, []);

  const pay = async (payment_type = "full") => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", { invoice_id: active.id, origin_url: window.location.origin, payment_type });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start payment"); setPaying(false); }
  };

  const BLOCKED = ["Void", "Cancelled", "Draft", "Estimate", "Disputed", "Refunded", "Archived", "Paid"];
  const isPayable = (iv) => iv && !BLOCKED.includes(iv.status) && (iv.balance_due || 0) > 0;

  return (
    <DashboardLayout nav={getNav(user.role)} title="Invoices">
      {!invs ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> :
        invs.length === 0 ? <div className="text-center py-20 bg-white rounded-xl border border-slate-200"><Receipt className="w-10 h-10 text-slate-300 mx-auto" /><p className="mt-3 text-slate-500">No invoices yet.</p></div> : (
          <div className="space-y-3">
            {invs.map((iv) => (
              <button key={iv.id} data-testid={`invoice-row-${iv.id}`} onClick={() => setActive(iv)} className="w-full text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-300 transition-colors flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2"><span className="font-medium text-slate-900">{iv.invoice_number}</span><StatusBadge status={iv.status} /></div>
                  <div className="text-sm text-slate-500 mt-1">{iv.property?.name} · {iv.work_order?.wo_number}</div>
                </div>
                <div className="text-right">
                  <div className="font-head text-xl font-semibold text-slate-900">{money(iv.total)}</div>
                  {iv.balance_due > 0 && <div className="text-xs text-rose-600">{money(iv.balance_due)} due</div>}
                </div>
              </button>
            ))}
          </div>
        )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-head">Invoice {active.invoice_number}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="text-xs text-slate-400">SJ Contract Solutions LLC</div>
                <div className="text-sm text-slate-600">{active.property?.name} · {active.work_order?.wo_number}</div>
                <div className="text-sm">{active.description}</div>
                <div className="space-y-1 text-sm border-t pt-3">
                  <Row l="Labor" v={money(active.labor)} />
                  <Row l="Materials" v={money(active.materials)} />
                  {active.additional_charges > 0 && <Row l="Additional Charges" v={money(active.additional_charges)} />}
                  {active.tax > 0 && <Row l="Tax" v={money(active.tax)} />}
                  <div className="flex justify-between pt-2 border-t font-head font-semibold text-lg"><span>Total</span><span>{money(active.total)}</span></div>
                  <Row l="Amount Paid" v={money(active.amount_paid)} />
                  <div className="flex justify-between font-semibold text-rose-600"><span>Balance Due</span><span>{money(active.balance_due)}</span></div>
                </div>
                <div className="flex items-center justify-between text-sm"><StatusBadge status={active.status} /><span className="text-slate-400">Due {active.due_date}</span></div>
                <Button data-testid="invoice-pdf" variant="outline" size="sm" className="w-full" onClick={() => openPdf(`/invoices/${active.id}/pdf`)}>Download PDF</Button>
                {user.role === "client" && !isViewer && isPayable(active) && (
                  <div className="space-y-2">
                    <Button data-testid="pay-invoice-btn" disabled={paying} onClick={() => pay("full")} className="w-full bg-blue-600 hover:bg-blue-700 h-11">
                      {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1" /> Pay {money(active.balance_due)}</>}
                    </Button>
                    {active.deposit_amount > 0 && active.deposit_amount < active.balance_due && (
                      <Button data-testid="pay-deposit-btn" variant="outline" disabled={paying} onClick={() => pay("deposit")} className="w-full h-11 border-blue-200 text-blue-700">
                        Pay Deposit {money(active.deposit_amount)}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
const Row = ({ l, v }) => <div className="flex justify-between text-slate-600"><span>{l}</span><span>{v}</span></div>;
